import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { App, Form, Input, Space, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { signIn } from '@/apis/user.api.ts';
import { getDeviceInfo } from '@/utils/device';

import AuthFormLayout from '../-components/AuthFormLayout';
import { useFormValidation } from '../-hooks/useFormValidation';
import { useSignSuccess } from './-sign-success';

export const Route = createFileRoute('/auth/sign-in')({
  component: SignInPage,
  validateSearch: z.object({
    code: z.string().nullish(),
    state: z.string().nullish(),
    redirect: z.string().nullish(),
  }),
});

function SignInPage() {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const { getRequiredRules } = useFormValidation();
  const { code, state, redirect: redirectTo } = Route.useSearch();
  const signSuccess = useSignSuccess();

  const loginMutation = useMutation({
    mutationFn: signIn,
  });

  const onFinish = async (values: any) => {
    const loginData: any = {
      ...values,
      ...getDeviceInfo(),
    };

    try {
      const user = await loginMutation.mutateAsync(loginData);
      await signSuccess(user, redirectTo);
    } catch (error: any) {
      message
        .error(t(error.response?.data?.message ?? 'auth.login.error'))
        .then();
    }
  };

  return (
    <AuthFormLayout<any>
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle')}
      submitText={t('auth.login.submit')}
      loading={loginMutation.isPending}
      onFinish={onFinish}
    >
      <Form.Item
        name="account"
        rules={[
          ...getRequiredRules('account'),
          {
            max: 255,
            message: t('auth.validation.account.maxLength'),
          },
        ]}
      >
        <Input
          size="large"
          prefix={<UserOutlined />}
          placeholder={t('auth.form.account.placeholder')}
        />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          ...getRequiredRules('password'),
          {
            max: 128,
            message: t('auth.validation.password.maxLength'),
          },
        ]}
      >
        <Input.Password
          size="large"
          prefix={<LockOutlined />}
          placeholder={t('auth.form.password.placeholder')}
        />
      </Form.Item>

      <Space
        size="middle"
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginTop: 8,
        }}
      >
        <Link
          from="/"
          to="/auth/reset-password"
          className="text-blue-600 transition-colors hover:text-blue-800"
        >
          {t('auth.links.forgotPassword')}
        </Link>
      </Space>
      {code || state ? (
        <div
          className={
            'absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white'
          }
        >
          <Spin />
        </div>
      ) : null}
    </AuthFormLayout>
  );
}
